import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  RefreshCw, 
  Pause, 
  Play, 
  Trash2, 
  Loader2,
  Calendar,
  User,
  AlertCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency, Currency } from "@/lib/currency";
import { format } from "date-fns";

interface RecurringCollectionsProps {
  merchantId: string;
  currency: Currency;
  terminology?: any;
}

interface RecurringSubscription {
  id: string;
  service_name: string;
  description: string | null;
  frequency: string;
  amount: number;
  currency: string;
  status: string;
  is_auto_recurring: boolean;
  next_due_date: string | null;
  start_date: string;
  created_at: string;
  client_id: string;
  client_name?: string;
  client_email?: string;
}

export function RecurringCollections({ merchantId, currency, terminology = {} }: RecurringCollectionsProps) {
  const [subscriptions, setSubscriptions] = useState<RecurringSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [cancelDialogId, setCancelDialogId] = useState<string | null>(null);

  useEffect(() => {
    fetchRecurring();
  }, [merchantId]);

  const fetchRecurring = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("client_subscriptions")
        .select("*, professional_clients(name, email)")
        .eq("merchant_id", merchantId)
        .eq("is_auto_recurring", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setSubscriptions(
        (data || []).map((s: any) => ({
          ...s,
          client_name: s.professional_clients?.name,
          client_email: s.professional_clients?.email,
        }))
      );
    } catch (error) {
      console.error("Error fetching recurring:", error);
      toast.error("Failed to load recurring collections");
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePause = async (sub: RecurringSubscription) => {
    setActionLoading(sub.id);
    const newStatus = sub.status === "paused" ? "active" : "paused";
    try {
      const { error } = await supabase
        .from("client_subscriptions")
        .update({ status: newStatus })
        .eq("id", sub.id);
      if (error) throw error;
      toast.success(newStatus === "paused" ? "Recurring paused" : "Recurring resumed");
      fetchRecurring();
    } catch (error: any) {
      toast.error(error.message || "Failed to update");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (id: string) => {
    setActionLoading(id);
    try {
      const { error } = await supabase
        .from("client_subscriptions")
        .update({ status: "cancelled", is_auto_recurring: false })
        .eq("id", id);
      if (error) throw error;
      toast.success("Recurring collection cancelled");
      setCancelDialogId(null);
      fetchRecurring();
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAutoRecurring = async (sub: RecurringSubscription, checked: boolean) => {
    setActionLoading(sub.id);
    try {
      const { error } = await supabase
        .from("client_subscriptions")
        .update({ is_auto_recurring: checked })
        .eq("id", sub.id);
      if (error) throw error;
      toast.success(checked ? "Auto-recurring enabled" : "Auto-recurring disabled");
      fetchRecurring();
    } catch (error: any) {
      toast.error(error.message || "Failed to update");
    } finally {
      setActionLoading(null);
    }
  };

  const getFrequencyLabel = (freq: string) => {
    const map: Record<string, string> = {
      weekly: "Weekly",
      monthly: "Monthly",
      quarterly: "Quarterly",
      annual: "Annual",
    };
    return map[freq] || freq;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-green-500/10 text-green-600 border-green-500/20";
      case "paused": return "bg-yellow-500/10 text-yellow-600 border-yellow-500/20";
      case "cancelled": return "bg-red-500/10 text-red-600 border-red-500/20";
      default: return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-cyan-600" />
            Recurring Collections
          </CardTitle>
          <CardDescription>
            Manage auto-recurring payment collections. These will automatically create new payment requests on the due date.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <div className="text-center py-8">
              <RefreshCw className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-medium mb-1">No recurring collections</h3>
              <p className="text-sm text-muted-foreground">
                Enable auto-recurring when creating a collection to see it here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {subscriptions.map((sub) => (
                <div
                  key={sub.id}
                  className="rounded-lg border p-4 space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h4 className="font-semibold text-sm">{sub.service_name}</h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <User className="h-3 w-3" />
                        {sub.client_name || sub.client_email}
                      </div>
                      {sub.description && (
                        <p className="text-xs text-muted-foreground">{sub.description}</p>
                      )}
                    </div>
                    <Badge className={getStatusColor(sub.status)}>
                      {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {formatCurrency(sub.amount, currency)} / {getFrequencyLabel(sub.frequency).toLowerCase()}
                    </span>
                    {sub.next_due_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Next: {format(new Date(sub.next_due_date), "dd MMM yyyy")}
                      </span>
                    )}
                  </div>

                  {sub.status !== "cancelled" && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={sub.is_auto_recurring}
                          onCheckedChange={(checked) => handleToggleAutoRecurring(sub, checked)}
                          disabled={actionLoading === sub.id}
                        />
                        <span className="text-xs text-muted-foreground">Auto-create</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => handleTogglePause(sub)}
                          disabled={actionLoading === sub.id}
                        >
                          {sub.status === "paused" ? (
                            <><Play className="h-3 w-3 mr-1" /> Resume</>
                          ) : (
                            <><Pause className="h-3 w-3 mr-1" /> Pause</>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs text-destructive hover:text-destructive"
                          onClick={() => setCancelDialogId(sub.id)}
                          disabled={actionLoading === sub.id}
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!cancelDialogId} onOpenChange={() => setCancelDialogId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Cancel Recurring Collection
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will stop all future auto-created payments for this collection. Existing pending payments will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Active</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => cancelDialogId && handleCancel(cancelDialogId)}
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
